import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { ArrowDown, ArrowRight, Heart } from "lucide-react";

import PosterPlaceholder from "../../components/ui/PosterPlaceholder";
import ShowPicker from "../../components/show-picker/ShowPicker";
import BrowsePageIntro from "../../components/BrowsePageIntro";
import Typography from "../../components/ui/Typography";
import useWatchlist from "../../hooks/useWatchlist";
import {
  ARCHIVE_SEARCH_INDEX_SCOPES,
  loadArchiveSearchIndex,
} from "../../services/search/archiveSearchIndex";
import { trackEvent } from "../../utils/analytics";
import { handleInternalLinkClick } from "../../utils/navigation";
import {
  getRecommendationDestination,
  getRecommendationSuggestionMeta,
  isRecommendationAnchor,
  mergeRecommendationCatalog,
  resolveMyListRecommendationAnchors,
  selectRecommendationStarters,
} from "./recommendationHub";

const EMPTY_INDEX = { shows: [] };

function getPoster(show) {
  return show?.posterMetadata?.thumbnail || show?.poster || null;
}

const RecommendationsHub = ({ rankedShows = [], updatedLabel = null, rotationDate = null }) => {
  const { entries, hasHydrated } = useWatchlist();
  const [indexState, setIndexState] = useState({ status: "loading", index: EMPTY_INDEX });

  useEffect(() => {
    let active = true;
    loadArchiveSearchIndex({ scope: ARCHIVE_SEARCH_INDEX_SCOPES.ALL })
      .then((index) => {
        if (active) setIndexState({ status: "ready", index });
      })
      .catch(() => {
        if (!active) return;
        setIndexState({ status: "error", index: EMPTY_INDEX });
        trackEvent("recommendation_index_error", {
          category: "load_failed",
          surface: "most_recommended_hub",
        });
      });
    return () => {
      active = false;
    };
  }, []);

  const pickerShows = useMemo(
    () => mergeRecommendationCatalog(indexState.index.shows, rankedShows),
    [indexState.index.shows, rankedShows]
  );
  const pickerReady = indexState.status === "ready"
    || (indexState.status === "error" && pickerShows.some(isRecommendationAnchor));
  const pickerShowsBySlug = useMemo(
    () => new Map(pickerShows.map((show) => [show.slug, show]).filter(([slug]) => slug)),
    [pickerShows]
  );
  const starterShows = useMemo(
    () => selectRecommendationStarters(rankedShows, 6, rotationDate),
    [rankedShows, rotationDate]
  );
  const myListAnchors = useMemo(() => {
    if (!pickerReady || !hasHydrated) return [];
    return resolveMyListRecommendationAnchors(entries, pickerShowsBySlug);
  }, [entries, hasHydrated, pickerReady, pickerShowsBySlug]);

  const followRecommendation = (event, show, source) => {
    const href = getRecommendationDestination(show);
    if (!href) return;
    trackEvent(source === "picker" ? "recommendation_anchor_select" : "recommendation_starting_point_click", {
      show_slug: show.slug,
      ...(source === "picker" ? { surface: "most_recommended_hub" } : { source }),
    });
    handleInternalLinkClick(event, href);
  };

  return (
    <section className="most-recommended-hub" aria-labelledby="recommendations-hub-title">
      <BrowsePageIntro
        id="recommendations-hub-title"
        variant="archive"
        title="Recommendations"
        description="Name a show you love to get recommendations with a similar mood, humour or pace. Each pick explains why it fits."
        actions={(
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-body-small font-semibold">
            <a href="#most-recommended-ranking-title" className="inline-flex items-center gap-2 text-primary underline-offset-4 hover:underline">
              See the most recommended shows
              <ArrowDown className="h-4 w-4" aria-hidden="true" />
            </a>
            <a
              href="/archive"
              onClick={(event) => handleInternalLinkClick(event, "/archive")}
              className="text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
            >
              Search by mood instead
            </a>
            {updatedLabel ? <span className="font-normal text-muted-foreground">Updated {updatedLabel}</span> : null}
          </div>
        )}
      />

      <div className="archive-discovery__search">
        <ShowPicker
          id="recommendation-anchor-picker"
          label="Name a show you love"
          placeholder="Search for a show"
          shows={pickerShows}
          loading={indexState.status === "loading"}
          disabled={!pickerReady}
          isEligible={isRecommendationAnchor}
          getSuggestionMeta={getRecommendationSuggestionMeta}
          getDestinationLabel={() => "See matches"}
          getHref={getRecommendationDestination}
          noResultsMessage="No matching show found. Try one of the starting points below."
          onSelect={(show, event) => followRecommendation(event, show, "picker")}
        />
      </div>

      {indexState.status === "error" ? (
        <Typography role="status" variant="body-small" className="mt-3 text-destructive">
          Show search is unavailable right now. Choose a starting point below or browse the recommendations list.
        </Typography>
      ) : null}

      {starterShows.length > 0 ? (
        <div className="mt-7">
          <Typography as="h2" variant="h2">Somewhere to start</Typography>
          <Typography variant="body" className="mt-1 text-muted-foreground">
            Choose a familiar show to see its strongest recommendations.
          </Typography>
          <nav
            className="mt-4 grid grid-flow-col auto-cols-[42%] gap-3 overflow-x-auto pb-3 sm:auto-cols-[27%] lg:grid-flow-row lg:auto-cols-auto lg:grid-cols-6 lg:overflow-visible"
            aria-label="Popular shows to start recommendations from"
          >
            {starterShows.map((show) => {
              const href = getRecommendationDestination(show);
              return (
                <a
                  key={show.slug}
                  href={href}
                  onClick={(event) => followRecommendation(event, show, "featured")}
                  className="group min-w-0 rounded-lg bg-card p-2 shadow-card-base transition-transform duration-normal ease-out hover:-translate-y-1 hover:shadow-card-hover focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring active:translate-y-0"
                >
                  <span className="block overflow-hidden rounded-md bg-muted">
                    <img
                      src={getPoster(show)}
                      alt=""
                      aria-hidden="true"
                      className="aspect-[2/3] w-full object-cover transition-transform duration-normal ease-out group-hover:scale-[1.03]"
                      loading="lazy"
                    />
                  </span>
                  <span className="mt-2 block truncate text-body-small font-semibold text-foreground">{show.title}</span>
                  <span className="mt-1 flex items-center justify-between gap-2 text-body-xs text-muted-foreground">
                    <span>See matches</span>
                    <ArrowRight className="h-3.5 w-3.5 shrink-0 text-primary transition-transform duration-fast ease-out group-hover:translate-x-0.5" aria-hidden="true" />
                  </span>
                </a>
              );
            })}
          </nav>
        </div>
      ) : null}

      {hasHydrated && pickerReady && myListAnchors.length > 0 ? (
        <div className="mt-6 rounded-lg border border-border/70 bg-card/50 p-4 sm:p-5">
          <div className="flex items-center gap-2">
            <Heart className="h-4 w-4 text-primary" aria-hidden="true" />
            <Typography as="h2" variant="h4">Start from My List</Typography>
          </div>
          <nav className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4" aria-label="Start recommendations from My List">
            {myListAnchors.map((show) => {
              const href = getRecommendationDestination(show);
              const poster = getPoster(show);
              return (
                <a
                  key={show.slug}
                  href={href}
                  onClick={(event) => followRecommendation(event, show, "watchlist")}
                  className="group flex min-h-16 items-center gap-3 rounded-md bg-background/80 p-2.5 text-foreground transition-transform duration-fast ease-out hover:-translate-y-0.5 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <span className="h-14 w-10 shrink-0 overflow-hidden rounded-sm bg-muted">
                    {poster ? <img src={poster} alt="" aria-hidden="true" className="h-full w-full object-cover" loading="lazy" /> : <PosterPlaceholder showId={show.slug} className="h-full w-full" />}
                  </span>
                  <span className="min-w-0 flex-1">
                    <strong className="block truncate text-body-small">{show.title}</strong>
                    <span className="mt-1 block text-body-xs text-muted-foreground">See matches</span>
                  </span>
                  <ArrowRight className="h-4 w-4 shrink-0 text-primary transition-transform duration-fast ease-out group-hover:translate-x-0.5" aria-hidden="true" />
                </a>
              );
            })}
          </nav>
        </div>
      ) : null}
    </section>
  );
};

RecommendationsHub.propTypes = {
  rankedShows: PropTypes.arrayOf(PropTypes.object),
  updatedLabel: PropTypes.string,
  rotationDate: PropTypes.string,
};

export default RecommendationsHub;
